import { App } from '../../App/App';
import { createBrowserRouter, RouteObject } from 'react-router-dom';

import { resolveEnv } from '@speeki/frontend-utils';

import { ErrorPage } from '@components/pages/ErrorPage/ErrorPage';

import { getPath } from '@services/RouteService/paths';
import { CompanyRoute } from '@services/RouteService/routes/CompanyRoute';
import { MfaRoute } from '@services/RouteService/routes/MfaRoute';
import { ParamsRoute } from '@services/RouteService/routes/ParamsRoute';
import { PublicRoute } from '@services/RouteService/routes/PublicRoute';

import { DeclareCredentialsPage } from '@domains/auth/pages/DeclareCredentialsPage/DeclareCredentialsPage';
import { EnterCodePage } from '@domains/auth/pages/EnterCodePage/EnterCodePage';
import { ForgotPasswordPage } from '@domains/auth/pages/ForgotPasswordPage/ForgotPasswordPage';
import { LoginDisclosuresPage } from '@domains/auth/pages/LoginDislosuresPage/LoginDisclosuresPage';
import { LoginGuestPage } from '@domains/auth/pages/LoginGuestPage/LoginGuestPage';
import { LoginMessagingPage } from '@domains/auth/pages/LoginMessagingPage/LoginMessagingPage';
import { LoginPage } from '@domains/auth/pages/LoginPage/LoginPage';
import { LoginSurveysGuestPage } from '@domains/auth/pages/LoginSurveysGuestPage/LoginSurveysGuestPage';
import { LoginSurveysPage } from '@domains/auth/pages/LoginSurveysPage/LoginSurveysPage';
import { RequestResetMfaPage } from '@domains/auth/pages/RequestResetMfaPage/RequestResetMfaPage';
import { ResetMfaPage } from '@domains/auth/pages/ResetMfaPage/ResetMfaPage';
import { ResetPasswordPage } from '@domains/auth/pages/ResetPasswordPage/ResetPasswordPage';
import { SetupMfaEmailPage } from '@domains/auth/pages/SetupMfaEmailPage/SetupMfaEmailPage';
import { SetupMfaPage } from '@domains/auth/pages/SetupMfaPage/SetupMfaPage';
import { SignUpPage } from '@domains/auth/pages/SignUpPage/SignUpPage';
import { StartNewReportGuestPage } from '@domains/auth/pages/StartNewReportGuestPage/StartNewReportGuestPage';
import { StartNewReportLoginPage } from '@domains/auth/pages/StartNewReportLoginPage/StartNewReportLoginPage';
import { SuccessfulMfaSetupPage } from '@domains/auth/pages/SuccessfulMfaSetupPage/SuccessfulMfaSetupPage';
import { WelcomePage } from '@domains/auth/pages/WelcomePage/WelcomePage';
import { CasesListPage } from '@domains/cases/pages/CasesListPage/CasesListPage';
import { CaseStatusPage } from '@domains/cases/pages/CaseStatusPage/CaseStatusPage';
import { MyReportPage } from '@domains/cases/pages/MyReportPage/MyReportPage';
import { DashboardPage } from '@domains/dashboard/DashboardPage';
import { SkeletonDashboardPage } from '@domains/dashboard/DashboardPage.skeleton';
import { DisclosuresListPage } from '@domains/disclosures/pages/DisclosuresListPage/DisclosuresListPage';
import { FilledDisclosuresListPage } from '@domains/disclosures/pages/FilledDisclosuresListPage/FilledDisclosuresListPage';
import { UnavailablePage } from '@domains/disclosures/pages/UnavailablePage/UnavailablePage';
import { DownloadCaseContentAccessPage } from '@domains/download-case-pdf/pages/DownloadCaseContentAccessPage/DownloadCaseContentAccessPage';
import { DownloadCaseContentFilePage } from '@domains/download-case-pdf/pages/DownloadCaseContentFilePage/DownloadCaseContentFilePage';
import { ConversationDetailsPage } from '@domains/new-conversation/pages/ConversationDetailsPage/ConversationDetailsPage';
import { ReportConversationPage } from '@domains/new-conversation/pages/ReportConversationPage/ReportConversationPage';
import { ReportReviewPage } from '@domains/new-conversation/pages/ReportReviewPage/ReportReviewPage';
import { ReportSetPinPage } from '@domains/new-conversation/pages/ReportSetPinPage/ReportSetPinPage';
import { ReportSubmittedPage } from '@domains/new-conversation/pages/ReportSubmittedPage/ReportSubmittedPage';
import { SettingsPage } from '@domains/settings/pages/SettingsPage/SettingsPage';
import { SurveyListPage } from '@domains/Survey/SurveyListPage/SurveyListPage';
import { SurveyUnavailablePage } from '@domains/Survey/SurveyUnavailablePage/SurveyUnavailablePage';
import loadable from '@loadable/component';
import * as Sentry from '@sentry/react';

const sentryCreateBrowserRouter =
    Sentry.wrapCreateBrowserRouter(createBrowserRouter);

const SubmissionDetailsPage = loadable(
    () =>
        import(
            '@domains/disclosures/pages/SubmissionDetailsPage/SubmissionDetailsPage'
        ),
    {
        resolveComponent: (component) => component.SubmissionDetailsPage,
    },
);

const SurveyDetailsPage = loadable(
    () => import('@domains/Survey/SurveyDetailsPage/SurveyDetailsPage'),
    {
        resolveComponent: (component) => component.SurveyDetailsPage,
    },
);

const DisclosureDetailsPage = loadable(
    () =>
        import(
            '@domains/disclosures/pages/DisclosureDetailsPage/DisclosureDetailsPage'
        ),
    {
        resolveComponent: (component) => component.DisclosureDetailsPage,
    },
);

const ThankYouPage = loadable(
    () => import('@domains/disclosures/pages/ThankYouPage/ThankYouPage'),
    {
        resolveComponent: (component) => component.ThankYouPage,
    },
);

const routes: RouteObject[] = [
    {
        children: [
            /* --- /auth --- */
            {
                element: (
                    <CompanyRoute users={['guest', 'notLogged']}>
                        <LoginGuestPage />
                    </CompanyRoute>
                ),
                path: '/auth/login/guest',
            },
            {
                element: (
                    <CompanyRoute users={['guest', 'notLogged']}>
                        <LoginPage />
                    </CompanyRoute>
                ),
                path: '/auth/login',
            },
            {
                element: (
                    <CompanyRoute users={['guest', 'notLogged']}>
                        <SignUpPage />
                    </CompanyRoute>
                ),
                path: '/auth/signup',
            },
            {
                element: (
                    <PublicRoute>
                        <WelcomePage />
                    </PublicRoute>
                ),
                path: '/auth/welcome',
            },
            /* --- /auth --- password */
            {
                element: (
                    <CompanyRoute users={['notLogged']}>
                        <ForgotPasswordPage />
                    </CompanyRoute>
                ),
                path: '/auth/forgot-password',
            },
            {
                element: (
                    <PublicRoute>
                        <ResetPasswordPage />
                    </PublicRoute>
                ),
                path: '/auth/password-reset/confirm',
            },
            /* --- /auth --- mfa */
            {
                element: (
                    <MfaRoute>
                        <EnterCodePage />
                    </MfaRoute>
                ),
                path: '/auth/enter-code',
            },
            {
                element: (
                    <MfaRoute>
                        <RequestResetMfaPage />
                    </MfaRoute>
                ),
                path: '/auth/request-reset-mfa',
            },
            {
                element: (
                    <PublicRoute>
                        <ResetMfaPage />
                    </PublicRoute>
                ),
                path: '/auth/reset-mfa',
            },
            {
                element: (
                    <MfaRoute>
                        <SetupMfaEmailPage />
                    </MfaRoute>
                ),
                path: '/auth/setup-mfa/recovery-email',
            },
            {
                element: (
                    <MfaRoute>
                        <SetupMfaPage />
                    </MfaRoute>
                ),
                path: '/auth/setup-mfa',
            },
            {
                element: (
                    <CompanyRoute users={['logged']}>
                        <SuccessfulMfaSetupPage />
                    </CompanyRoute>
                ),
                path: '/auth/successful-mfa-setup',
            },
            /* --- /auth --- */
            {
                element: (
                    <CompanyRoute users={['guest', 'notLogged']}>
                        <LoginDisclosuresPage />
                    </CompanyRoute>
                ),
                path: '/auth/declare-or-disclose-information',
            },
            {
                element: (
                    <CompanyRoute
                        modules={'cases'}
                        users={['guest', 'notLogged']}
                    >
                        <LoginMessagingPage />
                    </CompanyRoute>
                ),
                path: '/auth/see-my-incidents',
            },
            {
                element: (
                    <CompanyRoute users={['guest', 'notLogged']}>
                        <LoginSurveysGuestPage />
                    </CompanyRoute>
                ),
                path: '/auth/see-active-surveys/guest',
            },
            {
                element: (
                    <CompanyRoute users={['guest', 'notLogged']}>
                        <LoginSurveysPage />
                    </CompanyRoute>
                ),
                path: '/auth/see-active-surveys',
            },
            {
                element: (
                    <CompanyRoute users={['guest', 'notLogged']}>
                        <StartNewReportLoginPage />
                    </CompanyRoute>
                ),
                path: '/auth/start-new-report/login',
            },
            {
                element: (
                    <CompanyRoute users={['guest', 'notLogged']}>
                        <StartNewReportGuestPage />
                    </CompanyRoute>
                ),
                path: '/auth/start-new-report',
            },
            {
                element: (
                    <CompanyRoute users={['logged']}>
                        <DeclareCredentialsPage />
                    </CompanyRoute>
                ),
                path: '/auth/declare-credentials/:disclosureUuid',
            },
            /* --- /disclosure --- */
            {
                element: (
                    <CompanyRoute modules={'disclosures'} users={['logged']}>
                        <DisclosureDetailsPage
                            fallback={<SkeletonDashboardPage />}
                        />
                    </CompanyRoute>
                ),
                path: '/disclosure/details/:disclosureUuid',
            },
            {
                element: (
                    <CompanyRoute modules={'disclosures'} users={['logged']}>
                        <SubmissionDetailsPage
                            fallback={<SkeletonDashboardPage />}
                        />
                    </CompanyRoute>
                ),
                path: '/disclosure/list/:disclosureUuid/:submissionUuid',
            },
            {
                element: (
                    <CompanyRoute modules={'disclosures'} users={['logged']}>
                        <FilledDisclosuresListPage />
                    </CompanyRoute>
                ),
                path: '/disclosure/list/:disclosureUuid',
            },
            {
                element: (
                    <CompanyRoute
                        modules={'disclosures'}
                        users={['guest', 'logged']}
                    >
                        <DisclosuresListPage />
                    </CompanyRoute>
                ),
                path: '/disclosure/list',
            },
            {
                element: (
                    <CompanyRoute modules={'disclosures'} users={['logged']}>
                        <ThankYouPage
                            fallback={<SkeletonDashboardPage />}
                            formType="disclosure"
                        />
                    </CompanyRoute>
                ),
                path: '/disclosure/thank-you',
            },
            {
                element: (
                    <CompanyRoute modules={'disclosures'} users={['logged']}>
                        <UnavailablePage />
                    </CompanyRoute>
                ),
                path: '/disclosure/unavailable',
            },
            /* --- /survey --- */
            {
                element: (
                    <CompanyRoute
                        modules={'surveys'}
                        users={['guest', 'logged']}
                    >
                        <SurveyDetailsPage
                            fallback={<SkeletonDashboardPage />}
                        />
                    </CompanyRoute>
                ),
                path: '/survey/details/:surveyId',
            },
            {
                element: (
                    <CompanyRoute
                        modules={'surveys'}
                        users={['guest', 'logged']}
                    >
                        <SurveyListPage />
                    </CompanyRoute>
                ),
                path: '/survey/list',
            },
            {
                element: (
                    <CompanyRoute
                        modules={'surveys'}
                        users={['guest', 'logged']}
                    >
                        <ThankYouPage
                            fallback={<SkeletonDashboardPage />}
                            formType={'survey'}
                        />
                    </CompanyRoute>
                ),
                path: '/survey/thank-you',
            },
            {
                element: (
                    <CompanyRoute
                        modules={'surveys'}
                        users={['guest', 'logged']}
                    >
                        <SurveyUnavailablePage />
                    </CompanyRoute>
                ),
                path: '/survey/unavailable',
            },
            /* --- /messaging --- */
            {
                element: (
                    <CompanyRoute
                        modules={'cases'}
                        users={['guest', 'logged', 'notLogged']}
                    >
                        <MyReportPage />
                    </CompanyRoute>
                ),
                path: '/incidents/reports/:caseId',
            },
            {
                element: (
                    <CompanyRoute modules={'cases'} users={['logged']}>
                        <CasesListPage />
                    </CompanyRoute>
                ),
                path: '/incidents/reports',
            },
            {
                element: (
                    <CompanyRoute
                        modules={'cases'}
                        redirectTo={getPath({ path: '/incidents/reports' })}
                        users={['guest']}
                    >
                        <CaseStatusPage />
                    </CompanyRoute>
                ),
                path: '/incidents',
            },
            /* --- /new-conversation --- */
            {
                element: (
                    <CompanyRoute modules={'cases'} users={['guest', 'logged']}>
                        <ReportSetPinPage />
                    </CompanyRoute>
                ),
                path: '/new-conversation/:caseUuid/additional-information',
            },
            {
                element: (
                    <CompanyRoute modules={'cases'} users={['guest', 'logged']}>
                        <ReportConversationPage />
                    </CompanyRoute>
                ),
                path: '/new-conversation/:caseUuid/report',
            },
            {
                element: (
                    <CompanyRoute modules={'cases'} users={['guest', 'logged']}>
                        <ReportReviewPage />
                    </CompanyRoute>
                ),
                path: '/new-conversation/:caseUuid/review',
            },
            {
                element: (
                    <CompanyRoute modules={'cases'} users={['guest', 'logged']}>
                        <ReportSubmittedPage />
                    </CompanyRoute>
                ),
                path: '/new-conversation/:caseUuid/success',
            },
            {
                element: (
                    <CompanyRoute modules={'cases'} users={['guest', 'logged']}>
                        <ConversationDetailsPage />
                    </CompanyRoute>
                ),
                path: '/new-conversation',
            },
            /* --- rest --- */
            {
                element: (
                    <CompanyRoute users={['guest', 'logged']}>
                        <SettingsPage />
                    </CompanyRoute>
                ),
                path: '/settings',
            },
            {
                element: (
                    <ParamsRoute users={['guest', 'notLogged']}>
                        <DownloadCaseContentAccessPage />
                    </ParamsRoute>
                ),
                path: '/download/incidentpdf/:companyCode/:caseId',
            },
            {
                element: <DownloadCaseContentFilePage />,

                path: '/download/incidentpdf/file',
            },
            {
                element: (
                    <CompanyRoute users={['guest', 'logged', 'notLogged']}>
                        <DashboardPage />
                    </CompanyRoute>
                ),
                path: '/',
            },
            /* --- all --- */
            {
                element: <ErrorPage />,
                path: '*',
            },
        ],
        element: <App />,
        path: '/',
    },
];

const publicUrl = resolveEnv('PUBLIC_URL');

export const router = sentryCreateBrowserRouter(routes, {
    basename: publicUrl.endsWith('/') ? publicUrl : `${publicUrl}/`,
});
